import React, {useMemo, useState, useEffect, useCallback} from "react"
import {Card, Col, Container, Form, Row, Alert, Button} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import BreadCrumb from "../../../Common/BreadCrumb";
import DataTable from "../../../Common/DataTable";

import {mockStores} from "../../../data/stores";
import { EditStore } from "../Components/EditStore";
import {COLORS} from "../../../Common/constants/colors";
import {capitalizeFirstLetter} from "../../../utils/textUtils";
import {dashboardAPI} from "../../../services/api";

const StoreList = () => {
    const {t} = useTranslation();
    document.title = t('StoreList.title');
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [data, setData] = useState<Store[]>(mockStores);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch stores from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users
            setData(mockStores);
            return;
        }

        const fetchStores = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('📋 Fetching stores from backend...');
                const response: any = await dashboardAPI.getStores();
                
                // Normalize response - handle both AxiosResponse and direct data
                const stores = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Stores received:', stores.length, 'stores');
                
                // Map backend StoreDTO to frontend Store format
                const mappedStores: Store[] = stores.map((store: any) => ({
                    id: store.id || '',
                    name: store.name || '',
                    city: store.city || '',
                    province: store.province || '',
                    phone: store.phone || '',
                    address: store.address || '',
                    active: store.active !== undefined ? store.active : true,
                    createdOn: store.createdOn || new Date().toISOString(),
                    users: [] // Users not included in StoreDTO
                }));
                
                setData(mappedStores);
            } catch (error: any) {
                console.error('❌ Failed to fetch stores:', error);
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch stores');
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStores();
    }, [isDemoUser]);

    const handleEdit = (store: Store) => {
        setSelectedStore(store);
        setEditModalOpen(true);
    };

    const handleToggleActive = useCallback(async (store: Store, newActiveState: boolean) => {
        if (isDemoUser) {
            // For demo, just update local state
            setData(prevData => {
                const newData = [...prevData];
                const index = newData.findIndex(s => s.id === store.id);
                if (index !== -1) {
                    newData[index].active = newActiveState;
                }
                return newData;
            });
            return;
        }

        try {
            // Optimistically update UI
            setData(prevData => {
                const newData = [...prevData];
                const index = newData.findIndex(s => s.id === store.id);
                if (index !== -1) {
                    newData[index].active = newActiveState;
                }
                return newData;
            });

            // Update on backend
            await dashboardAPI.updateStore(store.id, {
                ...store,
                active: newActiveState
            });
        } catch (error: any) {
            console.error('Failed to update store active status:', error);
            // Revert on error
            setData(prevData => {
                const newData = [...prevData];
                const index = newData.findIndex(s => s.id === store.id);
                if (index !== -1) {
                    newData[index].active = !newActiveState;
                }
                return newData;
            });
            alert(error?.response?.data?.message || error?.message || 'Failed to update store status');
        }
    }, [isDemoUser]);


    const columns = useMemo(
        () => {
            const cols: any[] = [
                {
                    Header: t('StoreList.store_name'),
                    accessor: "name",
                    Cell: ({row, value}: any) => {
                        const store = data[row.index];
                        return (
                            <div style={{minWidth: 0}}>
                                <Link 
                                    to={`/store/${store.id}`} 
                                    style={{color: COLORS.LINK, textDecoration: "none", wordBreak: 'break-word'}}
                                >
                                    {value}
                                </Link>
                                {isMobile && (
                                    <div className="mt-1">
                                        <small className="text-muted">ID: {store.id}</small>
                                    </div>
                                )}
                            </div>
                        );
                    }
                },
                {
                    Header: t('StoreList.city'),
                    accessor: "city",
                    Cell: ({value}: any) => (
                        <div>{capitalizeFirstLetter(value)}</div>
                    )
                },
            ];
            
            // Add store_id only on desktop
            if (!isMobile) {
                cols.unshift({
                    Header: t('StoreList.store_id'),
                    accessor: "id",
                    Cell: ({value}: any) => (
                        <Link to={`/store/${value}`} style={{color: COLORS.LINK, textDecoration: "none"}}>
                            {value}
                        </Link>
                    ),
                });
            }
            
            // Add province only on desktop
            if (!isMobile) {
                cols.push({
                    Header: t('StoreList.province'),
                    accessor: "province",
                    Cell: ({value}: any) => (
                        <div>{capitalizeFirstLetter(value)}</div>
                    )
                });
            }
            
            cols.push(
                {
                    Header: t('StoreList.active'),
                    accessor: "active",
                    Cell: ({row, value}: any) => {
                        const store = data[row.index];
                        return (
                            <div className="form-check form-switch form-switch-custom form-switch-primary">
                                <Form.Check 
                                    onChange={() => handleToggleActive(store, !value)} 
                                    type="checkbox" 
                                    role="switch" 
                                    id={`store-switch-${store.id}`} 
                                    checked={value}
                                    disabled={isLoading}
                                />
                            </div>
                        );
                    },
                }
            );
            
            // Add created_on only on desktop
            if (!isMobile) {
                cols.push({
                    Header: t('StoreList.created_on'),
                    accessor: "createdOn",
                });
            }
            
            return cols;
        },
        [data, t, isLoading, isMobile, handleToggleActive]
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('StoreList.page_title'), path: "/stores"}
                        ]}
                    />
                    {error && (
                        <Row>
                            <Col>
                                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Body>
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : isMobile ? (
                                        // Mobile card view with pagination
                                        <>
                                            <div className="d-flex flex-column gap-3">
                                                {data.length === 0 ? (
                                                    <div className="text-center py-5 text-muted">
                                                        {t('StoreList.no_stores') || 'No stores found'}
                                                    </div>
                                                ) : (
                                                    data
                                                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                                        .map((store) => (
                                                            <Card key={store.id} className="shadow-sm">
                                                                <Card.Body>
                                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                                        <div className="flex-grow-1" style={{minWidth: 0}}>
                                                                            <Link 
                                                                                to={`/store/${store.id}`} 
                                                                                style={{color: COLORS.LINK, textDecoration: "none"}}
                                                                            >
                                                                                <h6 className="mb-1" style={{wordBreak: 'break-word'}}>
                                                                                    {store.name}
                                                                                </h6>
                                                                            </Link>
                                                                            <small className="text-muted d-block mb-2">
                                                                                ID: {store.id}
                                                                            </small>
                                                                            <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                                                                                <span className="badge bg-info">
                                                                                    {capitalizeFirstLetter(store.city)}
                                                                                </span>
                                                                                {store.province && (
                                                                                    <span className="badge bg-secondary">
                                                                                        {capitalizeFirstLetter(store.province)}
                                                                                    </span>
                                                                                )}
                                                                                <div className="form-check form-switch form-switch-custom form-switch-primary">
                                                                                    <Form.Check 
                                                                                        onChange={() => handleToggleActive(store, !store.active)} 
                                                                                        type="checkbox" 
                                                                                        role="switch" 
                                                                                        id={`store-switch-mobile-${store.id}`} 
                                                                                        checked={store.active}
                                                                                        disabled={isLoading}
                                                                                        label={<small>{t('StoreList.active') || 'Active'}</small>}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            {store.address && (
                                                                                <small className="text-muted d-block mt-2" style={{wordBreak: 'break-word'}}>
                                                                                    📍 {store.address}
                                                                                </small>
                                                                            )}
                                                                            {store.phone && (
                                                                                <small className="text-muted d-block mt-1">
                                                                                    📞 {store.phone}
                                                                                </small>
                                                                            )}
                                                                            {store.createdOn && (
                                                                                <small className="text-muted d-block mt-2">
                                                                                    📅 {typeof store.createdOn === 'string' ? store.createdOn.split('T')[0] : new Date(store.createdOn).toISOString().split('T')[0]}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleEdit(store)}
                                                                            className="w-100"
                                                                        >
                                                                            {t('StoreList.edit') || 'Edit'}
                                                                        </Button>
                                                                    </div>
                                                                </Card.Body>
                                                            </Card>
                                                        ))
                                                )}
                                            </div>
                                            {data.length > pageSize && (
                                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                                    <div className="text-muted small">
                                                        {t('StoreList.showing') || 'Showing'} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, data.length)} {t('StoreList.of') || 'of'} {data.length}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                            disabled={currentPage === 1}
                                                        >
                                                            {t('StoreList.previous') || 'Previous'}
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => setCurrentPage(prev => Math.min(Math.ceil(data.length / pageSize), prev + 1))}
                                                            disabled={currentPage >= Math.ceil(data.length / pageSize)}
                                                        >
                                                            {t('StoreList.next') || 'Next'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Desktop table view
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                title={t('StoreList.search_store')}
                                                editable={true}
                                                deletable={false}
                                                alertsDeletable={false}
                                                onEdit={handleEdit}
                                            />
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <EditStore
                        show={editModalOpen}
                        store={selectedStore}
                        onClose={() => setEditModalOpen(false)}
                        onSave={async (updatedStore: any) => {
                            if (isDemoUser) {
                                // For demo, just update local state
                                const newData = [...data];
                                const index = newData.findIndex(s => s.id === updatedStore.id);
                                if (index !== -1) {
                                    newData[index] = { ...newData[index], ...updatedStore };
                                    setData(newData);
                                }
                                setEditModalOpen(false);
                                return;
                            }

                            try {
                                // Update on backend
                                await dashboardAPI.updateStore(updatedStore.id, updatedStore);
                                
                                // Update local state
                                const newData = [...data];
                                const index = newData.findIndex(s => s.id === updatedStore.id);
                                if (index !== -1) {
                                    newData[index] = { ...newData[index], ...updatedStore };
                                    setData(newData);
                                }
                                
                                setEditModalOpen(false);
                            } catch (error: any) {
                                console.error('Failed to update store:', error);
                                alert(error?.response?.data?.message || error?.message || 'Failed to update store');
                            }
                        }}
                    />
                </Container>
            </div>
        </React.Fragment>
    )

}
export  default StoreList